import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Alert, App, Button, Result, Spin, Typography } from 'antd';
import { useAuth } from '@/features/auth/auth-context';
import { AuthLayout } from '@/features/auth/ui/AuthLayout';
import { ACTIVE_ORG_STORAGE_KEY, orgsQueryKey } from '../active-org-context';
import { organizationsApi } from '../organizations.api';

export function AcceptInvitationPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status } = useAuth();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const preview = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => organizationsApi.previewInvitation(token),
    enabled: Boolean(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => organizationsApi.acceptInvitation(token),
    onSuccess: async (result) => {
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, result.organizationId);
      await queryClient.invalidateQueries({ queryKey: orgsQueryKey });
      message.success(`You've joined ${result.organizationName}`);
      void navigate({ to: '/' });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not accept invitation'),
  });

  const redirectTarget = encodeURIComponent(`/invitations/accept?token=${token}`);

  let content: React.ReactNode;
  if (!token) {
    content = <Result status="error" title="Missing invitation token" />;
  } else if (preview.isLoading) {
    content = <Spin />;
  } else if (preview.isError || !preview.data) {
    content = (
      <Result
        status="error"
        title="Invitation invalid"
        subTitle="This invitation is invalid or has expired."
      />
    );
  } else {
    const invited = preview.data;
    content = (
      <>
        <Typography.Paragraph>
          You've been invited to join <strong>{invited.organizationName}</strong> as{' '}
          <strong>{invited.role}</strong> ({invited.email}).
        </Typography.Paragraph>
        {status === 'authenticated' ? (
          <Button type="primary" block loading={accept.isPending} onClick={() => accept.mutate()}>
            Accept invitation
          </Button>
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Sign in to accept"
              description={`Sign in (or create an account) using ${invited.email} to join.`}
            />
            <Button
              type="primary"
              block
              onClick={() => window.location.assign(`/login?redirect=${redirectTarget}`)}
            >
              Sign in to continue
            </Button>
          </>
        )}
      </>
    );
  }

  return <AuthLayout title="Join organization">{content}</AuthLayout>;
}
